import { FC, useEffect, useState } from 'react'
import { toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { useMembersAdminsRoles } from '../../../../hooks/membersAdmin/useMembersAdminsRoles'
import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik'
import { createMember, permissionByRole, updateMyPermission, updateProfilePicture } from '../../../../services/membersAdminService'
import * as Yup from 'yup'
import { showErrorAlert } from '../../../../helpers/alert'
import ImageField from '../../../teamAdmin/components/fields/ImageField'
import { handleApiError } from '../../../../utils/handleApiError'
import PhoneInput from 'react-phone-input-2'
import SVG from 'react-inlinesvg'
import { fleurSVGBlack, fleurSVGGray } from '../../../../utils/constants'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const AddMemberModal: FC<Props> = ({
  isOpen,
  onClose
}) => {
  const formik: any = useFormikContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [selectedRole, setSelectedRole] = useState<number>(1)
  const { data: roles, loading: rolesLoading } = useMembersAdminsRoles()
  const [sections, setSections] = useState<any[]>([])

  useEffect(() => {
    getPermissionByRole(selectedRole)
  }, [])

  const getPermissionByRole = async (id: number) => {
    let response = await permissionByRole(id);
    setSections(response)
  }

  const handleRoleChange = (value: number) => {
    getPermissionByRole(value)
    setSelectedRole(value);
  };

  if (!isOpen) return null

  const handleSectionChange = (
    moduleId: number,
    changes: Partial<Section>
  ) => {
    setSections(prev =>
      prev.map(section =>
        section.module === moduleId
          ? { ...section, ...changes }
          : section
      )
    )
  }

  return (
    <div className='nb-modal-overlay' onClick={onClose}>
      <div className='nb-invite-modal' onClick={(e) => e.stopPropagation()}>
        {/* CLOSE BUTTON */}
        <div className="admin-experience-details-header">
          <h2 className="nb-invite-modal__title">
            Add Admin
          </h2>
          <div className="rm-style-3 cursor-pointer" onClick={onClose}>
            <SVG src={toAbsoluteUrl("/media/svg/nobilis/closeX.svg")} className="rm-style-4" />
          </div>
        </div>

        <Formik
          enableReinitialize
          initialValues={{
            profile_picture: null,
            first_name: '',
            last_name: '',
            phone_number: '',
            email: '',
          }}
          validationSchema={Yup.object({
            profile_picture: Yup.mixed()
              .required("Profile image is required")
              .test("fileOrUrl", "Only images or valid URLs allowed", (value) => {
                if (!value) return false;

                // Caso 1: File
                if (value instanceof File) {
                  return ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(value.type);
                }

                // Caso 2: URL string
                if (typeof value === "string") {
                  try {
                    const url = new URL(value);
                    return true; // si parsea bien, es URL válida
                  } catch {
                    return false;
                  }
                }

                return false;
              })
              .test("fileSize", "Max size is 5MB", (value) => {
                if (value instanceof File) {
                  return value.size <= 5 * 1024 * 1024;
                }
                return true; // si es URL, no aplica límite de tamaño
              }),
            first_name: Yup.string().required("Name is required").max(100, "Max 100 characters"),
            last_name: Yup.string().required("Last name is required").max(100, "Max 100 characters"),
            phone_number: Yup.string().matches(/^[0-9()+\-\s]{7,15}$/, 'Invalid phone number').required('Phone is required'),
            email: Yup.string().email('Wrong email format').min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Email is required'),
          })}
          onSubmit={async (values: any, actions: any) => {
            if (step === 1) {
              setStep(2);
              actions.setSubmitting(false);
              return;
            }

            try {
              setLoading(true)
              //return;
              const payload = { ...values, roleIds: [7], role_id: '7', status: 'active' };

              await createMember(payload)
                .then(async (resp: any) => {
                  if (values.profile_picture && values.profile_picture instanceof File) {
                    const formData = new FormData();
                    formData.append("profile_picture", values.profile_picture);
                    await updateProfilePicture(formData, resp.id)
                    let updatePermission = await updateMyPermission(resp.id,sections)
                  }
                  setLoading(false)
                  showErrorAlert({ title: 'Success', message: 'User added successfully' })
                  onClose()
                })
                .catch((error) => {
                  setLoading(false);
                  handleApiError(error, {
                    onServerError: () => showErrorAlert({ title: 'Error', message: 'Error creating the admin. Please try again later.' }),
                  });
                });

              setLoading(false)
            } catch (error: any) {
              setLoading(false)
              const statusCode = error?.response?.status || 500
            }
          }}
        >
          {({ values, setFieldValue, validateForm, setTouched }) => {
            const exampleImages = ['/media/people3.png', '/media/people2.png', '/media/people1.png'];

            return (
              <Form className='profile-admin-form  profile-form-base' style={{ display: 'contents' }}>
                <div className="team-admin-add-member__card">
                  {step === 2 && (
                    <>
                      <div className="team-admin-add-member__field">
                        <label>Photo</label>
                        <ImageField />
                        <ErrorMessage name="profile_picture" component="div" className="tap-add-experience-text text-danger" />
                      </div>
                      {/* Examples Section */}
                      <div className='photo-examples-section mt-5'>
                        <div className='examples-group'>
                          <label className='examples-label'>Example</label>
                          <div className={`examples-images`}>
                            {exampleImages.map((src, index) => (
                              <div key={index} className={`example-image ${index === 1 ? 'bw' : ''}`}>
                                <img src={toAbsoluteUrl(src)} alt={`Example ${index + 1}`} />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Requirements */}
                        <div className='photo-requirements'>
                          <div className='requirements-left'>
                            <div className='requirement-row'>
                              <div className='check-icon'>✓</div>
                              <span>High-resolution, professional headshot</span>
                            </div>
                            <div className='requirement-row'>
                              <div className='check-icon'>✓</div>
                              <span>Clear focus on face</span>
                            </div>
                            <div className='requirement-row'>
                              <div className='check-icon'>✓</div>
                              <span>Neutral or calm background with proper lighting</span>
                            </div>
                          </div>

                          <div className='requirements-right'>
                            <div className='requirement-row'>
                              <div className='check-icon'>✓</div>
                              <span>Recent image</span>
                            </div>
                            <div className='requirement-row'>
                              <div className='check-icon'>✓</div>
                              <span>Format: JPEG/PNG, max 5MB</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {step === 1 && (
                    <>
                      <div className='form-field'>
                        <label className='field-label'>Name</label>
                        <Field name='first_name' className='input' />
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block input-text-style fs-8'>
                            <ErrorMessage name='first_name' />
                          </div>
                        </div>
                      </div>

                      <div className='form-field'>
                        <label className='field-label'>Surname</label>
                        <Field name='last_name' className='input' />
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block input-text-style fs-8'>
                            <ErrorMessage name='last_name' />
                          </div>
                        </div>
                      </div>

                      <div className='form-field'>
                        <label className='field-label'>Phone Number</label>
                        <div className="tap-add-experience-2-input-line">
                          <Field name='phone_number'>
                            {({ field, form }: any) => (
                              <PhoneInput
                                country='us'
                                placeholder='+1'
                                containerClass='profile-input'
                                inputClass='phone-input-custom'
                                value={field.value || ''}
                                onChange={(val: string) => form.setFieldValue(field.name, val)}
                                onBlur={() => form.setFieldTouched(field.name, true)}
                              />
                            )}
                          </Field>
                        </div>
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block input-text-style fs-8'>
                            <ErrorMessage name='phone_number' />
                          </div>
                        </div>
                      </div>

                      <div className='form-field'>
                        <label className='field-label'>Email</label>
                        <Field name='email' className='input' />
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block input-text-style fs-8'>
                            <ErrorMessage name='email' />
                          </div>
                        </div>
                      </div>

                      <div className='form-field'>
                        <label className='field-label'>Role</label>
                        <div className='w-100 d-flex flex-row'>
                          <div className='d-flex flex-row w-100 gap-4'>
                            <SectionRole item={{moduleName:'Admin'}} key={`item-button-admin-1`} selected={selectedRole == 1} icon={false} handleClick={()=>handleRoleChange(1)} />
                            <SectionRole item={{moduleName:'Staff'}} key={`item-button-admin-2`} selected={selectedRole == 7} icon={false} handleClick={()=>handleRoleChange(7)} />
                          </div>
                        </div>
                        {/* contenido */}
                        <div className='w-100 d-flex flex-row'>
                          <div className='d-flex flex-column w-100'>
                            <span className='admin-roles-users__header'>Sections</span>
                            {(sections ?? []).map(section =>
                              <SectionRole item={section} key={`item-role-admin-${section.module}`} handleClick={()=>{}} />
                            )}
                          </div>
                          <div className='d-flex flex-column w-100'>
                            <span className='admin-roles-users__header'>All actions (create/edit/delete)</span>
                            {(sections ?? []).map(
                              section => (
                                <div key={`item-role-admin-${section.module}-2`} className='admin-roles-users__row pf-checkbox'>
                                  <input type="checkbox" className="pf-check" checked={section.canCreate && section.canDelete && section.canEdit}
                                    onChange={(e) =>
                                      handleSectionChange(section.module, {
                                        canCreate: e.target.checked,
                                        canEdit: e.target.checked,
                                        canDelete: e.target.checked,
                                        onlyOwnRecords: !e.target.checked,
                                      })
                                    }
                                    />
                                </div>
                              ))}
                          </div>
                          <div className='d-flex flex-column w-100'>
                            <span className='admin-roles-users__header'>Only Assigned (edit)</span>
                            {(sections ?? []).map(
                              section => (
                                <div key={`item-role-admin-${section.module}-4`} className='admin-roles-users__row pf-checkbox'>
                                  <input type="checkbox" className="pf-check" checked={section?.onlyOwnRecords}
                                    onChange={(e) =>
                                      handleSectionChange(section.module, {
                                        canCreate: !e.target.checked,
                                        canEdit: e.target.checked,
                                        canDelete: !e.target.checked,
                                        onlyOwnRecords: e.target.checked,
                                      })
                                    }
                                    />
                                </div>
                              ))}
                          </div>
                          <div className='d-flex flex-column w-100'>
                            <span className='admin-roles-users__header'>View Sensitive Data</span>
                            {(sections ?? []).map(
                              section => (
                                <div key={`item-role-admin-${section.module}-5`} className='admin-roles-users__row pf-checkbox'>
                                  <input type="checkbox" className="pf-check" checked={section?.canViewSensitiveInfo}
                                    onChange={(e) =>
                                      handleSectionChange(section.module, {
                                        canViewSensitiveInfo: e.target.checked,
                                      })
                                    }
                                    />
                                </div>
                              ))}
                          </div>
                        </div>

                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block input-text-style fs-8'>
                            <ErrorMessage name='contact_role' />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Buttons */}
                <div className='form-actions'>
                  <button
                    type='button'
                    className='btn-secondary'
                    onClick={() => {
                      if (step === 2) {
                        setStep(1);
                      } else {
                        onClose();
                      }
                    }}
                  >
                    Cancel
                  </button>

                  {step === 1 ? (
                    <button
                      type='button'
                      className='btn nb-btn-primary'
                      onClick={async (e) => {
                        e.preventDefault();
                        const errors = await validateForm();
                        setTouched({
                          first_name: true,
                          last_name: true,
                          email: true,
                          phone_number: true
                        });
                        if (!errors.first_name && !errors.last_name && !errors.email && !errors.phone_number) {
                          setStep(2);
                        }
                      }}
                    >
                      <span className='nb-heading-md'>Next</span>
                      <img
                        src='/media/svg/nobilis/vector1.svg'
                        alt=''
                        className='nb-btn-icon nb-btn-icon--white'
                      />
                    </button>
                  ) : (
                    <button
                      type='submit'
                      className='btn nb-btn-primary'
                      disabled={loading}
                      aria-busy={loading ? 'true' : 'false'}
                      aria-live='polite'
                    >
                      {!loading ? (
                        <>
                          <span className='nb-heading-md'>Save changes</span>
                          <img
                            src='/media/svg/nobilis/vector1.svg'
                            alt=''
                            className='nb-btn-icon nb-btn-icon--white'
                          />
                        </>
                      ) : (
                        <span className='indicator-progress nb-heading-md'>
                          Please wait...
                          <span
                            className='spinner-border spinner-border-sm align-middle ms-2'
                            role='status'
                            aria-hidden='true'
                          />
                        </span>
                      )}
                    </button>
                  )}
                </div>

              </Form>
            )
          }}
        </Formik>

      </div>

    </div>
  )
}

interface Props {
    item: any,
    key:string,
    selected?:boolean,
    icon?:boolean,
    handleClick:()=>void
}

export const SectionRole:FC<Props> = ({ item, key, selected=false, icon=true, handleClick}) => {
  return (
    <div key={key} className={`admin-section-roles-item ${selected ? 'admin-section-roles-item__category-item--selected' : '' }`} onClick={handleClick}>
      {icon && <SVG src={selected ? fleurSVGBlack : fleurSVGGray} height={20} width={20} />}
      <div className='admin-section-roles-item__text'>{item?.moduleName}</div>
    </div>
  )
}

export default AddMemberModal
